import { useEffect, useState } from "react";
import { Download, Smartphone, X, Share, Plus } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const DISMISS_KEY = "nexafx:install-banner-dismissed";

export function InstallAppBanner() {
  const { user } = useAuth();
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);
  const [isIos, setIsIos] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    setDismissed(localStorage.getItem(DISMISS_KEY) === "1");

    const ua = window.navigator.userAgent.toLowerCase();
    const iOS = /iphone|ipad|ipod/.test(ua) && !(window as any).MSStream;
    setIsIos(iOS);

    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as any).standalone === true;
    setIsStandalone(standalone);

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  if (!user || isStandalone || dismissed) return null;
  // Need either a deferred prompt (Android/Chrome/Edge) OR iOS Safari
  if (!deferred && !isIos) return null;

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  const handleInstall = async () => {
    if (deferred) {
      await deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice.outcome === "accepted") {
        setDeferred(null);
        handleDismiss();
      }
    } else if (isIos) {
      setShowIosHelp((v) => !v);
    }
  };

  return (
    <div className="border-b border-primary/20 bg-gradient-to-r from-primary/[0.08] via-primary/[0.04] to-transparent">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-3 px-3 py-2">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/15 text-primary">
          <Smartphone className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-foreground sm:text-sm">
            Install NexaFX as an app
          </p>
          <p className="text-[11px] text-muted-foreground sm:text-xs">
            {isIos
              ? "Add to your home screen for instant access — works offline."
              : "One-tap install on your phone or desktop. Faster, fullscreen, offline-ready."}
          </p>
        </div>
        <button
          type="button"
          onClick={handleInstall}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-[11px] font-semibold text-primary-foreground transition-all hover:brightness-110 sm:text-xs"
        >
          <Download className="h-3.5 w-3.5" />
          {isIos ? "How to install" : "Install app"}
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {showIosHelp && isIos && (
        <div className="mx-auto max-w-7xl border-t border-primary/15 bg-card/60 px-3 py-3 text-xs text-muted-foreground">
          <ol className="space-y-1.5">
            <li className="flex items-center gap-2">
              <span className="font-mono text-primary">1.</span>
              <span>Open this page in <span className="font-semibold text-foreground">Safari</span></span>
            </li>
            <li className="flex items-center gap-2">
              <span className="font-mono text-primary">2.</span>
              <span className="inline-flex items-center gap-1">
                Tap the <Share className="inline h-3.5 w-3.5" /> <span className="font-semibold text-foreground">Share</span> button
              </span>
            </li>
            <li className="flex items-center gap-2">
              <span className="font-mono text-primary">3.</span>
              <span className="inline-flex items-center gap-1">
                Choose <Plus className="inline h-3.5 w-3.5" /> <span className="font-semibold text-foreground">Add to Home Screen</span>
              </span>
            </li>
          </ol>
        </div>
      )}
    </div>
  );
}
